// Sprint-V25 Issue L (Sa 12.09.2026) — useHookVariant()
// Bestimmt die Hero-Hook-Variante fuer die Landing-Page.
// Prioritaet:
//   1. Query-Parameter `?hook=a|b|c|d`   (Vorrang; wird auch persistiert)
//   2. Cookie `f4s_hook_variant`         (sticky 30 Tage)
//   3. Zufallszuweisung                  (gleichverteilt A|B|C|D)
//
// Vertrag laut Briefing (CHAT02 Launch-CoS):
//   - Query hat Vorrang vor Cookie (Override moeglich, z.B. fuer Preview-Links).
//   - Cookie 30 Tage sticky, damit wiederkehrende Besucher die gleiche
//     Variante sehen und die Conversion-Rate pro Visitor konsistent
//     gemessen werden kann.
//   - Rueckgabe ist immer einer aus {"a","b","c","d"} — nie null.

import { useEffect, useState } from "react";
import { HEADLINE_CANDIDATES } from "@/data/landingHookCopy";
import { isPreviewRuntime } from "@/lib/trafficSource";

export const HOOK_VARIANTS = ["a", "b", "c", "d"];
const COOKIE_NAME = "f4s_hook_variant";
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 Tage

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const raw = document.cookie || "";
  for (const part of raw.split(";")) {
    const [k, ...rest] = part.trim().split("=");
    if (k === name) return decodeURIComponent(rest.join("=") || "");
  }
  return null;
}

function writeCookie(name, value) {
  if (typeof document === "undefined") return;
  document.cookie =
    `${name}=${encodeURIComponent(value)}` +
    `; Max-Age=${COOKIE_MAX_AGE_SECONDS}` +
    `; Path=/` +
    `; SameSite=Lax`;
}

// Sprint-V25 Issue L (Sa 12.09.2026 spät) — Curiosity-Rotation gemäß
// Russell (Expert Secrets LIVE Day 3 Section 3): C ist Curiosity-stärkste
// Variante, wird bei Erstbesuch überproportional ausgespielt.
// 02.10.2026 Hotfix (A/B-Winner-Shift): Hook D ("Wirkung") hat sich im
// Live-Traffic als Winner herausgestellt (laut extern-tracked Zahlen
// 7,1 % Conversion vs. 2,5 %/3,3 %/4,1 %). Gewicht verschoben:
// A=8% · B=8% · C=8% · D=76% (ca. 75 % Winner-Push, 25 % für
// kontinuierliches Challenger-Signal). Query ?hook=a|b|c|d bleibt
// voll funktionsfähig für Preview-Links.
const HOOK_WEIGHTS = { a: 0.08, b: 0.08, c: 0.08, d: 0.76 };

function pickRandom() {
  const r = Math.random();
  let acc = 0;
  for (const k of HOOK_VARIANTS) {
    acc += HOOK_WEIGHTS[k] || 0;
    if (r < acc) return k;
  }
  return "c"; // Safety net
}

// Kandidaten q01..q10: nur Vorschau, nur Arbeitsspeicher (kein Cookie).
let memoryCandidate = null;
let lastAssignment = null;
export const getHookAssignment = () => lastAssignment;

/** Resolve the hook variant using the priority: query > cookie > random. */
export function resolveHookVariant() {
  if (typeof window === "undefined") return "a";
  const params = new URLSearchParams(window.location.search || "");
  const q = (params.get("hook") || "").toLowerCase();
  if (isPreviewRuntime() && !HOOK_VARIANTS.includes(q)) {
    if (HEADLINE_CANDIDATES[q]) {
      memoryCandidate = q;
      lastAssignment = "query";
      return q;
    }
    if (memoryCandidate) {
      lastAssignment = "memory";
      return memoryCandidate;
    }
  }
  if (HOOK_VARIANTS.includes(q)) {
    writeCookie(COOKIE_NAME, q);
    lastAssignment = "query";
    return q;
  }
  const c = (readCookie(COOKIE_NAME) || "").toLowerCase();
  if (HOOK_VARIANTS.includes(c)) {
    lastAssignment = "stored";
    return c;
  }
  const rnd = pickRandom();
  writeCookie(COOKIE_NAME, rnd);
  lastAssignment = "new";
  return rnd;
}

export default function useHookVariant() {
  const [variant, setVariant] = useState(() => {
    if (typeof window === "undefined") return "a";
    return resolveHookVariant();
  });
  useEffect(() => {
    // Falls die Query nachtraeglich hinzugefuegt/entfernt wird
    // (Navigation innerhalb der Landing-Page), Variante neu bestimmen.
    setVariant(resolveHookVariant());
  }, []);
  return variant;
}
